import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { addAssetIdea, normalizeIdeaSymbol } from '../lib/assetIdeas.js'

export function useAddAssetIdea(userId, onSaved) {
  const owner = useRef(userId)
  owner.current = userId
  const onSavedRef = useRef(onSaved)
  onSavedRef.current = onSaved
  const pending = useRef(new Map())
  const [notice, setNotice] = useState(null)
  const noticeSequence = useRef(0)
  const { id: noticeId, tone: noticeTone, userId: noticeUserId } = notice || {}
  useEffect(() => {
    if (!noticeId || noticeTone === 'error' || noticeUserId !== userId) return undefined
    const fade = setTimeout(() => {
      setNotice(current => current?.id === noticeId ? { ...current, fading: true } : current)
    }, 2000)
    const dismiss = setTimeout(() => {
      setNotice(current => current?.id === noticeId ? null : current)
    }, 2250)
    return () => { clearTimeout(fade); clearTimeout(dismiss) }
  }, [noticeId, noticeTone, noticeUserId, userId])
  const publish = useCallback(value => {
    if (owner.current === userId) setNotice({ ...value, userId, id: ++noticeSequence.current })
  }, [userId])
  const add = useCallback(async value => {
    let symbol
    try { symbol = normalizeIdeaSymbol(value) } catch (error) {
      publish({ tone: 'error', text: error.message })
      return { ok: false }
    }
    const key = `${userId}:${symbol}`
    if (pending.current.has(key)) return pending.current.get(key)
    const operation = (async () => {
      try {
        const result = await addAssetIdea(supabase, userId, symbol)
        if (owner.current !== userId) return { ok: false }
        onSavedRef.current?.(result)
        publish({ tone: 'success', text: result.stage === 'idea'
          ? (result.created ? `${symbol} added to Ideas` : `${symbol} is already in Ideas`)
          : `${symbol} is already ${result.stage === 'active' ? 'Active' : 'Working'}` })
        return { ok: true }
      } catch (error) {
        publish({ tone: 'error', text: error.message })
        return { ok: false }
      } finally { pending.current.delete(key) }
    })()
    pending.current.set(key, operation)
    return operation
  }, [publish, userId])
  return { add, notice: notice?.userId === userId ? notice : null, dismissNotice: () => setNotice(null) }
}
